import React from 'react';
import { ExternalLink, Download } from 'lucide-react';
import { normalizeVideoUrl } from '../../types/lms';

interface LessonResourcesProps {
  raw: string;
}

interface ResourceLink {
  label: string;
  url: string;
}

export function parseResources(raw: string): ResourceLink[] {
  if (!raw) return [];
  const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const out: ResourceLink[] = [];
  lines.forEach((line, i) => {
    // Supported: "Label | URL", "Label — URL", "Label: URL", or a bare URL.
    const pipe = line.split('|');
    let label = '';
    let target = '';
    if (pipe.length >= 2) {
      label = pipe[0].trim();
      target = pipe.slice(1).join('|').trim();
    } else {
      const colon = line.match(/^(.+?)\s*[—–:]\s*(https?:\/\/\S+|\/\S+)$/);
      if (colon) {
        label = colon[1].trim();
        target = colon[2].trim();
      } else if (/^https?:\/\//i.test(line) || line.startsWith('/')) {
        target = line;
      }
    }
    const url = target.startsWith('/') ? target : normalizeVideoUrl(target);
    if (!url || (!/^https?:\/\//i.test(url) && !url.startsWith('/'))) return;
    if (!label) {
      try {
        const u = new URL(url);
        const file = u.pathname.split('/').filter(Boolean).pop();
        label = decodeURIComponent(file || u.hostname);
      } catch {
        label = `Resource ${i + 1}`;
      }
    }
    out.push({ label, url });
  });
  return out;
}

const LessonResources: React.FC<LessonResourcesProps> = ({ raw }) => {
  const links = parseResources(raw);
  if (links.length === 0) return null;
  return (
    <div className="mt-4 border-y border-rule py-3">
      <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-ink-2">Lesson resources</p>
      <ul className="mt-2 grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
        {links.map((l) => {
          const isPdf = /\.pdf($|\?)/i.test(l.url);
          return (
            <li key={`${l.url}-${l.label}`}>
              <a
                href={l.url}
                target="_blank"
                rel="noreferrer"
                className="group inline-flex items-start gap-2 text-[12.5px] font-medium leading-5 text-pine hover:underline"
              >
                {isPdf ? (
                  <Download className="mt-0.5 h-3.5 w-3.5 flex-none" strokeWidth={1.5} />
                ) : (
                  <ExternalLink className="mt-0.5 h-3.5 w-3.5 flex-none" strokeWidth={1.5} />
                )}
                <span className="underline decoration-pine/25 underline-offset-4 group-hover:decoration-pine">{l.label}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default LessonResources;
